import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { LogOut, Truck, PackageCheck, CalendarClock, Plus, Loader2, Inbox, Package } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BRAND } from "@/data";

const STATUS_STYLE = {
  Delivered: { icon: PackageCheck, cls: "text-emerald-400 border-emerald-400/40 bg-emerald-400/10" },
  "In Transit": { icon: Truck, cls: "text-gold border-gold/40 bg-gold/10" },
  Scheduled: { icon: CalendarClock, cls: "text-sky-300 border-sky-300/40 bg-sky-300/10" },
};

export default function Dashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [shipments, setShipments] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [busy, setBusy] = useState(true);
  const isAdmin = user?.role === "admin";

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const reqs = [api.get("/shipments")];
      if (isAdmin) reqs.push(api.get("/quotes"));
      const [s, q] = await Promise.all(reqs);
      setShipments(s.data);
      if (q) setQuotes(q.data);
    } finally {
      setBusy(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [loading, user, navigate]);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-ink flex items-center justify-center">
        <Loader2 className="animate-spin text-gold" size={30} />
      </div>
    );
  }

  return (
    <div className="grain min-h-screen bg-ink">
      <div className="radial-purple absolute inset-0 pointer-events-none" />
      <header className="relative border-b border-gold/15 bg-ink/80 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-[1200px] mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={BRAND.logo} alt="Peace Trucking" className="h-10 w-10 object-contain" />
            <span className="font-serif text-xl text-cream">Peace Trucking</span>
          </Link>
          <div className="flex items-center gap-5">
            <div className="text-right hidden sm:block">
              <div className="text-sm text-cream">{user.name}</div>
              <div className="text-[11px] tracking-widest uppercase text-gold/70">{user.role}</div>
            </div>
            <button onClick={() => { logout(); navigate("/"); }} data-testid="logout-btn" className="flex items-center gap-2 border border-gold/30 text-gold px-4 py-2 text-sm btn-gold">
              <LogOut size={16} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="relative max-w-[1200px] mx-auto px-6 py-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <h1 className="font-serif text-4xl lg:text-5xl text-cream font-light">
            {isAdmin ? "Operations" : "Welcome back,"}{" "}
            <span className="italic text-gold">{isAdmin ? "Dashboard" : user.name.split(" ")[0]}</span>
          </h1>
          <p className="mt-3 text-cream/55 font-light">
            {isAdmin ? "Manage shipments and incoming quote requests." : "Track your active and past shipments below."}
          </p>
        </motion.div>

        {isAdmin && <AdminCreate onCreated={load} />}

        <Section title={isAdmin ? "All Shipments" : "Your Shipments"} icon={Package}>
          {busy ? (
            <Loading />
          ) : shipments.length === 0 ? (
            <Empty text="No shipments yet. New shipments will appear here." />
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {shipments.map((s, i) => (
                <ShipmentCard key={s.id} s={s} i={i} isAdmin={isAdmin} onUpdate={load} />
              ))}
            </div>
          )}
        </Section>

        {isAdmin && (
          <Section title="Quote Requests" icon={Inbox}>
            {busy ? <Loading /> : quotes.length === 0 ? (
              <Empty text="No quote requests yet." />
            ) : (
              <div className="space-y-3">
                {quotes.map((q, i) => (
                  <motion.div
                    key={q.id}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.04 }}
                    data-testid="quote-row"
                    className="bg-ink-2 border border-gold/10 p-5 grid sm:grid-cols-4 gap-3"
                  >
                    <div>
                      <div className="text-cream">{q.name}</div>
                      <div className="text-cream/50 text-sm">{q.email}</div>
                    </div>
                    <div className="text-cream/70 text-sm">{q.service}</div>
                    <div className="text-cream/70 text-sm">{q.pickup || "—"} → {q.destination || "—"}</div>
                    <div className="text-cream/50 text-sm sm:text-right">{q.phone || q.organization}</div>
                    {q.message && <div className="sm:col-span-4 text-cream/50 text-sm border-t border-gold/10 pt-3">{q.message}</div>}
                  </motion.div>
                ))}
              </div>
            )}
          </Section>
        )}
      </main>
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <section className="mt-14">
      <div className="flex items-center gap-3 mb-6">
        <Icon size={20} className="text-gold" />
        <h2 className="font-serif text-2xl text-cream">{title}</h2>
      </div>
      {children}
    </section>
  );
}

function ShipmentCard({ s, i, isAdmin, onUpdate }) {
  const style = STATUS_STYLE[s.status] || STATUS_STYLE["Scheduled"];
  const Icon = style.icon;
  const [saving, setSaving] = useState(false);

  const cycle = async () => {
    const order = ["Scheduled", "In Transit", "Delivered"];
    const next = order[(order.indexOf(s.status) + 1) % order.length];
    setSaving(true);
    try {
      await api.patch(`/shipments/${s.id}`, { status: next });
      onUpdate();
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: i * 0.05 }}
      data-testid="shipment-card"
      className="bg-ink-2 border border-gold/10 p-6 hover:border-gold/30 transition-colors"
    >
      <div className="flex items-center justify-between">
        <span className="font-serif text-xl text-gold">{s.reference}</span>
        <span className={`flex items-center gap-2 text-xs tracking-widest uppercase border px-3 py-1 ${style.cls}`}>
          <Icon size={14} /> {s.status}
        </span>
      </div>
      <div className="mt-4 text-cream font-light">{s.service}</div>
      <div className="mt-3 flex items-center gap-2 text-sm text-cream/60">
        <span>{s.origin}</span>
        <span className="text-gold">→</span>
        <span>{s.destination}</span>
      </div>
      <div className="mt-3 text-sm text-cream/50">ETA: {s.eta || "—"}</div>
      {s.notes && <div className="mt-3 text-sm text-cream/40 border-t border-gold/10 pt-3">{s.notes}</div>}
      {isAdmin && (
        <button onClick={cycle} disabled={saving} data-testid="cycle-status" className="mt-4 text-xs tracking-widest text-gold border border-gold/30 px-4 py-2 btn-gold">
          {saving ? "..." : "ADVANCE STATUS"}
        </button>
      )}
    </motion.div>
  );
}

function AdminCreate({ onCreated }) {
  const empty = { client_email: "", service: "", origin: "", destination: "", status: "Scheduled", eta: "", notes: "" };
  const [form, setForm] = useState(empty);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const field = "w-full bg-ink border border-gold/20 px-3 py-2 text-cream text-sm placeholder:text-cream/30 focus:outline-none focus:ring-2 focus:ring-gold/60";

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/shipments", form);
      setForm(empty);
      setOpen(false);
      onCreated();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-10">
      <button onClick={() => setOpen(!open)} data-testid="new-shipment-toggle" className="flex items-center gap-2 border border-gold/40 text-gold px-5 py-3 text-sm tracking-widest btn-gold">
        <Plus size={16} /> NEW SHIPMENT
      </button>
      {open && (
        <motion.form
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          onSubmit={submit}
          className="mt-4 bg-ink-2 border border-gold/15 p-6 grid sm:grid-cols-2 gap-3"
        >
          <input required type="email" placeholder="Client email" value={form.client_email} onChange={set("client_email")} className={field} data-testid="ns-email" />
          <input required placeholder="Service" value={form.service} onChange={set("service")} className={field} data-testid="ns-service" />
          <input required placeholder="Origin" value={form.origin} onChange={set("origin")} className={field} data-testid="ns-origin" />
          <input required placeholder="Destination" value={form.destination} onChange={set("destination")} className={field} data-testid="ns-destination" />
          <select value={form.status} onChange={set("status")} className={field} data-testid="ns-status">
            <option className="bg-ink">Scheduled</option>
            <option className="bg-ink">In Transit</option>
            <option className="bg-ink">Delivered</option>
          </select>
          <input placeholder="ETA" value={form.eta} onChange={set("eta")} className={field} data-testid="ns-eta" />
          <textarea placeholder="Notes" value={form.notes} onChange={set("notes")} className={`${field} sm:col-span-2`} data-testid="ns-notes" />
          <button type="submit" disabled={saving} className="sm:col-span-2 btn-gold border border-gold text-gold py-3 text-sm tracking-widest" data-testid="ns-submit">
            {saving ? "SAVING..." : "CREATE SHIPMENT"}
          </button>
        </motion.form>
      )}
    </div>
  );
}

const Loading = () => (
  <div className="flex justify-center py-12"><Loader2 className="animate-spin text-gold" size={26} /></div>
);
const Empty = ({ text }) => (
  <div className="bg-ink-2 border border-gold/10 p-10 text-center text-cream/50 font-light">{text}</div>
);
